<template>
  <NavBarSettings>
    <!-- Back to the workspace dashboard the user came from (TRV-1206); falls
         back to the workspace chooser ("All workspaces") when there's no recent
         workspace to return to. -->
    <template #top>
      <NuxtLink
        v-tooltip.right="navCollapsed ? backLabel : undefined"
        class="group flex w-full items-center gap-1.5 rounded-lg p-2 text-surface-700 transition-colors hover:bg-surface-100 hover:text-surface-900"
        :class="navCollapsed ? 'justify-center' : ''"
        :to="backTo"
        data-testid="settings-back-link"
      >
        <AppIcon
          icon="fa-solid fa-chevron-left"
          class="text-sm text-surface-500"
        />
        <span
          v-if="!navCollapsed"
          class="truncate text-sm font-medium"
          >{{ backLabel }}</span
        >
      </NuxtLink>
    </template>

    <!-- Organisation-settings navigation — one route per section (studio-design
         `organisation` sidebar variant). -->
    <template #nav-items>
      <div class="flex flex-col gap-1.5">
        <NavBarSettingsItem
          v-for="item in openableSections"
          :key="item.name"
          :label="item.label"
          :icon="item.icon"
          :to="{ name: item.name, params: { organizationSlug } }"
          :testid="`settings-nav-${item.name}`"
        />
      </div>
    </template>

    <template #footer>
      <NavBarAccountButton />
    </template>
  </NavBarSettings>
</template>

<script setup lang="ts">
import { useLayoutPreferencesStore } from '~/stores/layoutPreferences'
import { Permission } from '~/types/v2/Access'

const route = useRoute()

// Always the flat variant → collapses to a rail when the layout is minimized.
// Computed locally because NavBar (the provider) is a child of this component.
const layoutStore = useLayoutPreferencesStore()
const navCollapsed = computed(() => layoutStore.currentLayout === 'float')

const organizationSlug = computed(() => {
  const param = route.params.organizationSlug
  return typeof param === 'string' ? param : ''
})

const { backTo, backLabel } = useSettingsBackDestination(organizationSlug)

// Each section lists the permission its page declares; the sidebar shows only
// the ones the person can open (07.permission refuses the rest).
const { organization } = useCurrentOrganization()
const scope = () => ({ organizationId: organization.value?.id })

const sections = [
  {
    name: 'organizationSlug-settings-summary',
    label: 'Summary',
    icon: 'fa-regular fa-chart-tree-map',
    canOpen: useCan(Permission.OrganizationSettingsManage, scope),
  },
  {
    name: 'organizationSlug-settings-general',
    label: 'General',
    icon: 'fa-regular fa-gear',
    canOpen: useCan(Permission.OrganizationSettingsManage, scope),
  },
  {
    name: 'organizationSlug-settings-workspaces',
    label: 'Workspaces',
    icon: 'fa-regular fa-grid',
    canOpen: useCan(Permission.OrganizationWorkspacesManage, scope),
  },
  {
    name: 'organizationSlug-settings-users',
    label: 'Users',
    icon: 'fa-regular fa-user-group',
    canOpen: useCan(Permission.OrganizationMembersManage, scope),
  },
]

const openableSections = computed(() =>
  sections.filter((section) => section.canOpen.value),
)
</script>
